/**
 * LevelUpModal - Celebration modal when player levels up
 * Shows confetti, old → new level transition, and celebration message
 */

import React, { useEffect, useState } from 'react';
import './LevelUpModal.css';
import { trackLevelUp } from '../../../utils/posthogEvents';

/**
 * LevelUpModal component
 * @param {object} props
 * @param {string} props.studentId - Student ID for analytics
 * @param {object} props.previousLevel - Previous level object
 * @param {object} props.newLevel - New level object
 * @param {function} props.onClose - Close callback
 */
export default function LevelUpModal({ studentId, previousLevel, newLevel, onClose }) {
  const [showContent, setShowContent] = useState(false);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Stagger the animations
    const contentTimer = setTimeout(() => setShowContent(true), 200);
    const buttonTimer = setTimeout(() => setShowButton(true), 1000);

    // Fire analytics event once on mount
    if (previousLevel && newLevel) {
      trackLevelUp({
        studentId,
        newLevelName: newLevel.name,
        previousLevelName: previousLevel.name,
      });
    }

    return () => {
      clearTimeout(contentTimer);
      clearTimeout(buttonTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!previousLevel || !newLevel) return null;

  const isRainbow = newLevel.variant === 'rainbow';

  return (
    <div className="levelup-modal-overlay" onClick={onClose}>
      <div className="levelup-modal" onClick={(e) => e.stopPropagation()}>
        {/* Confetti particles */}
        <div className="confetti-container">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className={`confetti confetti-${i % 6}`}
              style={{
                '--delay': `${Math.random() * 0.5}s`,
                '--x-end': `${(Math.random() - 0.5) * 200}px`,
                '--rotation': `${Math.random() * 720 - 360}deg`,
              }}
            />
          ))}
        </div>

        {/* Title */}
        <div className={`levelup-title${showContent ? ' visible' : ''}`}>
          <span className="levelup-sparkle">✨</span>
          LEVEL UP!
          <span className="levelup-sparkle">✨</span>
        </div>

        {/* Level transition */}
        <div className={`levelup-levels${showContent ? ' visible' : ''}`}>
          {/* Previous level (small) */}
          <div className="levelup-prev">
            <div
              className="levelup-icon small"
              style={{
                background: `linear-gradient(145deg, ${previousLevel.colorLight} 0%, ${previousLevel.color} 100%)`,
                boxShadow: `0 2px 0 ${previousLevel.colorDark}`,
              }}
            >
              <span>{previousLevel.icon}</span>
            </div>
            <span className="levelup-level-name">{previousLevel.name}</span>
          </div>

          {/* Arrow */}
          <div className="levelup-arrow">→</div>

          {/* New level (large, animated) */}
          <div className="levelup-new">
            <div
              className={`levelup-icon large${isRainbow ? ' rainbow' : ''}`}
              style={!isRainbow ? {
                background: `linear-gradient(145deg, ${newLevel.colorLight} 0%, ${newLevel.color} 100%)`,
                boxShadow: `0 4px 0 ${newLevel.colorDark}, 0 8px 24px ${newLevel.color}60`,
              } : undefined}
            >
              <span>{newLevel.icon}</span>
            </div>
            <span className="levelup-level-name highlight">{newLevel.name}</span>
          </div>
        </div>

        {/* Subtitle */}
        <p className={`levelup-subtitle${showContent ? ' visible' : ''}`}>
          恭喜你升级了！继续加油！
        </p>

        {/* Continue button */}
        <button
          className={`levelup-button${showButton ? ' visible' : ''}`}
          onClick={onClose}
        >
          继续冒险！
        </button>
      </div>
    </div>
  );
}
